import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthProvider'
import { ApiError } from '@/shared/api/envelope'
import { CompanyLogo, PageSpinner, SipayLogo } from '@/shared/ui/Brand'
import { AppFooter } from '@/shared/ui/AppFooter'
import { Alert, Button, Checkbox, Field, Spinner } from '@/shared/ui/primitives'
import { Sky } from '@/shared/ui/Sky'
import { GREETING, useJakartaNow } from '@/shared/timeOfDay'
import { getConfig } from '@/shared/config'
import { Icon } from '@/app/icons'
import { rememberedUser } from './rememberedUser'

/**
 * Login: the sky on the left, the form on the right.
 *
 * The left panel is the same sky Beranda opens with, by the same clock, so
 * signing in and landing read as one product rather than a grey gate in front
 * of a colourful app. It holds only the greeting and the slogan -- no feature
 * list, no data: nothing on this side of the login may depend on who is
 * looking. On a phone it shrinks to a band above the form.
 */
export default function LoginPage() {
  const { user, login, initialising } = useAuth()
  const { tod } = useJakartaNow()
  const { companyName } = getConfig()
  const navigate = useNavigate()
  const location = useLocation()

  // Read once, at mount. Reading on every render would fight the user the
  // moment they clear the field to type a different address.
  const [email, setEmail] = useState(() => rememberedUser.read())
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(() => rememberedUser.read() !== '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<{ message: string; traceId?: string | null } | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  // Read from the keystroke itself, the only place the browser reports it.
  // Shown while typing the password, where a stuck Caps Lock turns a correct
  // password into "salah kata sandi" -- and the user cannot see why, because
  // the field shows dots.
  const [capsLock, setCapsLock] = useState(false)
  const readCapsLock = (event: KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(event.getModifierState('CapsLock'))

  const next = (location.state as { next?: string } | null)?.next ?? '/'

  if (initialising) return <PageSpinner />
  if (user) return <Navigate to={next} replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})

    // Check the obvious locally so an empty form never costs a round trip.
    // The server validates again regardless; this is only about feedback speed.
    const local: Record<string, string> = {}
    if (!email.trim()) local.email = 'Email wajib diisi'
    if (!password) local.password = 'Kata sandi wajib diisi'
    if (Object.keys(local).length > 0) {
      setFieldErrors(local)
      return
    }

    setSubmitting(true)
    try {
      // The plaintext password goes over TLS. The SPA deliberately does not
      // pre-hash it the way the legacy login form does: pre-hashing is what
      // made the stored value a directly replayable credential.
      await login(email.trim(), password, rememberMe)
      // Only after the credentials proved good. Remembering an address that
      // was just rejected would prefill a typo forever.
      if (rememberMe) rememberedUser.remember(email)
      else rememberedUser.forget()
      navigate(next, { replace: true })
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null

      if (apiError?.hasFieldErrors) {
        setFieldErrors(apiError.fieldErrors ?? {})
      } else {
        // The trace id is a support reference, so it only belongs on failures
        // the user cannot act on. Printing a UUID under "wrong password" just
        // invites the question "what is that code?" and makes an ordinary typo
        // look like a system fault.
        const isSystemFailure = !apiError || apiError.httpStatus >= 500
        setError({
          message: apiError?.resMsg ?? 'Tidak dapat terhubung ke server.',
          traceId: isSystemFailure ? apiError?.traceId : null,
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // On a phone the sky row takes only what it needs and the form row the
    // rest; without explicit rows the grid would split the height evenly.
    <div className="grid min-h-full grid-rows-[auto_1fr] lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:grid-rows-none">
      <Sky
        tod={tod}
        aria-label="SIPAY"
        markClassName="size-56 -right-10 -bottom-16 lg:size-[28rem] lg:-right-20 lg:-bottom-32"
        className="flex flex-col justify-between gap-5 px-4 pt-5 pb-7 sm:px-8 lg:gap-10 lg:px-14 lg:pt-10 lg:pb-9"
      >
        {/* On a phone the corporate mark shares the top row with SIPAY, so it
            stays on screen without making the band taller; from lg it moves
            to the foot of the panel with the company name beside it. */}
        <div className="flex items-center justify-between gap-4">
          <SipayLogo className="h-7 brightness-0 invert lg:h-9" />
          <CompanyLogo className="h-8 rounded-md bg-white p-1 lg:hidden" />
        </div>

        <div className="max-w-xl animate-rise">
          <p className="text-sm font-medium text-white/80 lg:text-base">{GREETING[tod]}</p>
          <p className="mt-1 text-[1.7rem] leading-[1.06] font-extrabold tracking-[-0.03em] text-balance text-white lg:mt-2.5 lg:text-[clamp(2.25rem,4.2vw,3.5rem)]">
            Semua dokumen tagihan, dalam satu tempat.
          </p>
        </div>

        <div className="hidden items-center gap-3 text-[0.8rem] leading-snug text-white/80 lg:flex">
          <CompanyLogo className="h-9 rounded-md bg-white p-1" />
          <span>
            Aplikasi penagihan
            <br />
            {companyName}
          </span>
        </div>
      </Sky>

      <div className="flex min-w-0 flex-col">
        <main className="grid flex-1 justify-items-center px-4 py-8 sm:px-8 lg:place-items-center lg:py-10">
          <div className="w-full max-w-100 animate-rise">
            <h1 className="text-[1.65rem] font-extrabold tracking-tight text-gray-900">Masuk</h1>
            <p className="mt-1 mb-6 text-gray-500">Gunakan email dan kata sandi SIPAY Anda.</p>

            <form onSubmit={onSubmit} noValidate>
              {error && (
                <Alert>
                  {error.message}
                  {error.traceId && (
                    <span className="mt-1.5 block font-mono text-xs opacity-75">
                      Sampaikan kode ini ke tim IT: {error.traceId}
                    </span>
                  )}
                </Alert>
              )}

              <Field
                id="email"
                label="Email"
                type="email"
                autoComplete="username"
                // Autofocus the field that is actually empty. For someone who is
                // remembered, that is the password.
                autoFocus={email === ''}
                value={email}
                error={fieldErrors.email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <Field
                id="password"
                label="Kata Sandi"
                type="password"
                autoComplete="current-password"
                autoFocus={email !== ''}
                value={password}
                error={fieldErrors.password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={readCapsLock}
                onKeyUp={readCapsLock}
                onBlur={() => setCapsLock(false)}
              />
              {capsLock && (
                <p
                  role="status"
                  className="-mt-3 mb-5 flex items-center gap-2 rounded-md bg-warning-light px-3 py-2 text-sm font-medium text-warning-ink"
                >
                  <Icon name="capsLock" className="size-4 shrink-0" />
                  Caps Lock aktif
                </p>
              )}

              <div className="mb-6">
                <Checkbox
                  id="rememberMe"
                  label="Ingat saya"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                {/* Said plainly, because the honest version of this checkbox is
                    a warning as much as a convenience: on a shared machine it
                    leaves the next person one click from this account. */}
                <p className="mt-1.5 pl-6.5 text-xs text-gray-500">
                  Sesi bertahan hingga 14 hari. Jangan aktifkan di komputer bersama.
                </p>
              </div>

              <Button type="submit" disabled={submitting} className="w-full">
                {submitting && <Spinner className="size-4 border-2 border-white/40 border-t-white" />}
                {submitting ? 'Memproses…' : 'Masuk'}
              </Button>
            </form>

            {/* No self-service reset here: resetting a password is an
                administrator's job in this organisation. */}
            <p className="mt-5 border-t border-line pt-4 text-sm text-gray-500">
              Lupa kata sandi? <b className="font-semibold text-gray-900">Hubungi administrator SIPAY</b> di unit
              Anda.
            </p>
          </div>
        </main>
        <AppFooter />
      </div>
    </div>
  )
}
